// src/pages/Consultation/tabs/OrderReviewTab.jsx - COMPLETE WITH ALL SCENARIOS
import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiShoppingCart,
  FiPackage,
  FiDollarSign,
  FiUser,
  FiPhone,
  FiMail,
  FiTag,
  FiAlertCircle,
  FiCheckCircle,
  FiLock,
  FiClock,
  FiPercent,
  FiRefreshCw,
  FiExternalLink,
} from "react-icons/fi";
import toast from "react-hot-toast";
import Button from "../../../../components/common/Button";
import OrderPlacementModal from "../../components/OrderPlacementModal";
import {
  createOrderFromSessionThunk,
  calculatePricingThunk,
} from "../../../../store/thunks/consultationThunks";

const OrderReviewTab = ({
  session,
  sessionPricing,
  cartItems = [],
  exchangeItems = [],
  customer,
  adminInfo,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { primaryBranch } = useSelector((state) => state.branch);
  const [showPlacementModal, setShowPlacementModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [refreshingPricing, setRefreshingPricing] = useState(false);

  // ✅ Check if session is converted
  const isConverted =
    session?.status === "CONVERTED" && session?.outcome?.converted;
  const orderId = session?.outcome?.orderId;

  // ✅ Debug logging
  useEffect(() => {
    console.log("📊 OrderReviewTab State:", {
      hasSessionPricing: !!sessionPricing,
      sessionPricingSummary: sessionPricing?.summary,
      cartItemsCount: cartItems?.length || 0,
      exchangeItemsCount: exchangeItems?.length || 0,
      sessionId: session?._id,
      isConverted,
      orderId,
    });
  }, [sessionPricing, cartItems, exchangeItems, session, isConverted, orderId]);

  // ✅ Navigate to order details
  const handleViewOrderDetails = () => {
    if (orderId) {
      navigate(`/orders/${orderId}`);
    }
  };

  // ✅ Manual pricing refresh
  const handleRefreshPricing = async () => {
    if (refreshingPricing || !session?._id) return;

    setRefreshingPricing(true);
    try {
      console.log("🔄 Manually refreshing pricing for session:", session._id);
      await dispatch(calculatePricingThunk(session._id)).unwrap();
      toast.success("Pricing refreshed successfully");
    } catch (error) {
      console.error("Refresh pricing error:", error);
      toast.error(error?.message || "Failed to refresh pricing");
    } finally {
      setRefreshingPricing(false);
    }
  };

  // ✅ Detect payment scenario
  const paymentScenario = useMemo(() => {
    const netPayable = sessionPricing?.summary?.netPayable || 0;
    const orderTotal = sessionPricing?.summary?.grandTotal || 0;
    const exchangeValue = sessionPricing?.summary?.totalExchangeValue || 0;

    const hasProducts = cartItems && cartItems.length > 0;
    const hasExchange = exchangeItems && exchangeItems.length > 0;

    let transactionType;
    if (!hasProducts && hasExchange) {
      transactionType = "GOLD_PURCHASE";
    } else if (hasProducts && hasExchange) {
      transactionType = "EXCHANGE_SALE";
    } else {
      transactionType = "SALE";
    }

    console.log("💰 Payment Scenario:", {
      transactionType,
      netPayable,
      orderTotal,
      exchangeValue,
      hasProducts,
      hasExchange,
    });

    if (netPayable < 0) {
      return {
        type: "CUSTOMER_REFUND",
        label: "Customer Receives Refund",
        refundAmount: Math.abs(netPayable),
        description:
          transactionType === "GOLD_PURCHASE"
            ? "Gold purchase - customer selling to business"
            : "Exchange value exceeds order total",
        color: "orange",
        icon: FiRefreshCw,
        transactionType,
      };
    } else if (netPayable === 0) {
      return {
        type: "ZERO_PAYMENT",
        label: "No Payment Required",
        description: "Exchange value covers entire order",
        color: "green",
        icon: FiCheckCircle,
        transactionType,
      };
    } else {
      return {
        type: "CUSTOMER_PAYS",
        label: "Customer Pays",
        amount: netPayable,
        description: "Normal payment required",
        color: "blue",
        icon: FiDollarSign,
        transactionType,
      };
    }
  }, [sessionPricing, cartItems, exchangeItems]);

  // ✅ Check if requires approval
  const requiresRefundApproval = useMemo(() => {
    if (paymentScenario.type !== "CUSTOMER_REFUND") return false;
    const APPROVAL_THRESHOLD = 10000;
    return paymentScenario.refundAmount > APPROVAL_THRESHOLD;
  }, [paymentScenario]);

  // ✅ IMPROVED: Validation checks
  const validationStatus = useMemo(() => {
    const issues = [];
    const warnings = [];

    const hasCartItems = cartItems && cartItems.length > 0;
    const hasExchangeItems = exchangeItems && exchangeItems.length > 0;

    // ✅ Must have EITHER cart items OR exchange items
    if (!hasCartItems && !hasExchangeItems) {
      issues.push("No items to order - add products or exchange items");
    }

    // ✅ For non-gold-purchase, check cart
    if (paymentScenario.transactionType !== "GOLD_PURCHASE" && !hasCartItems) {
      issues.push("Cart is empty");
    }

    // ✅ For gold purchase, check exchange items
    if (
      paymentScenario.transactionType === "GOLD_PURCHASE" &&
      !hasExchangeItems
    ) {
      issues.push("Exchange items required for gold purchase");
    }

    // ✅ Check pricing
    if (!sessionPricing) {
      issues.push("Pricing not calculated - please wait or refresh");
    } else if (!sessionPricing.summary) {
      issues.push("Pricing summary missing - please refresh");
    }

    // Check customer info
    const isAnonymous = !session?.userId;
    if (isAnonymous && !session?.guestCustomer?.name) {
      issues.push("Customer information required");
    }

    // Check price lock
    const isLocked = session?.priceLock?.isLocked;
    if (!isLocked) {
      warnings.push("Prices are not locked - they may change");
    }

    // Check pending adjustments
    const pendingAdj = session?.priceAdjustments?.filter(
      (adj) => adj.status === "PENDING"
    );
    if (pendingAdj && pendingAdj.length > 0) {
      issues.push(`${pendingAdj.length} adjustments pending approval`);
    }

    // Check session status
    if (!["ACTIVE", "PAUSED"].includes(session?.status) && !isConverted) {
      issues.push("Session must be active or paused");
    }

    // ✅ Warn about refund approval
    if (requiresRefundApproval) {
      warnings.push(
        `Refund amount (₹${paymentScenario.refundAmount.toLocaleString()}) requires manager approval`
      );
    }

    console.log("✅ Validation Status:", {
      isValid: issues.length === 0,
      issues,
      warnings,
    });

    return {
      isValid: issues.length === 0,
      issues,
      warnings,
      canPlaceOrder: issues.length === 0,
    };
  }, [
    session,
    sessionPricing,
    cartItems,
    exchangeItems,
    paymentScenario,
    requiresRefundApproval,
    isConverted,
  ]);

  const handlePlaceOrder = () => {
    if (!validationStatus.canPlaceOrder) {
      toast.error("Please resolve all issues before placing order");
      return;
    }
    setShowPlacementModal(true);
  };

  const handleConfirmOrder = async (orderConfig) => {
    setProcessing(true);
    try {
      await dispatch(
        createOrderFromSessionThunk({
          sessionId: session._id,
          orderData: {
            ...orderConfig,
            adminCode: adminInfo.adminCode,
            adminName: adminInfo.adminName,
          },
        })
      ).unwrap();

      toast.success("Order placed successfully!");
      setShowPlacementModal(false);
    } catch (error) {
      toast.error(error || "Failed to place order");
    } finally {
      setProcessing(false);
    }
  };

  const summary = sessionPricing?.summary || {};
  const isAnonymous = !session?.userId;
  const customerInfo = isAnonymous
    ? session?.guestCustomer
    : customer || session?.userId;

  // ✅ If session is converted, show order summary instead
  if (isConverted) {
    return (
      <ConvertedSessionView
        session={session}
        paymentScenario={paymentScenario}
        cartItems={cartItems}
        exchangeItems={exchangeItems}
        customerInfo={customerInfo}
        isAnonymous={isAnonymous}
        summary={summary}
        onViewOrderDetails={handleViewOrderDetails}
      />
    );
  }

  // ✅ Original view for active sessions
  return (
    <div className="space-y-4">
      {/* Header with validation status */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FiShoppingCart className="text-primary" />
              Order Review
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Review all details before placing the order
            </p>
          </div>

          {/* Validation Badge */}
          <div className="flex flex-col items-end gap-2">
            {validationStatus.isValid ? (
              <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                <FiCheckCircle />
                Ready to Place
              </span>
            ) : (
              <span className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                <FiAlertCircle />
                {validationStatus.issues.length} Issues
              </span>
            )}

            {/* ✅ Refresh Pricing Button */}
            {(!sessionPricing || !sessionPricing.summary) && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleRefreshPricing}
                disabled={refreshingPricing}
                className="text-xs"
              >
                <FiRefreshCw
                  className={`mr-1 ${refreshingPricing ? "animate-spin" : ""}`}
                />
                {refreshingPricing ? "Calculating..." : "Calculate Pricing"}
              </Button>
            )}

            {session?.priceLock?.isLocked && (
              <span className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                <FiLock size={12} />
                Prices Locked
              </span>
            )}
          </div>
        </div>

        {/* Issues & Warnings */}
        {(validationStatus.issues.length > 0 ||
          validationStatus.warnings.length > 0) && (
          <div className="mt-4 space-y-2">
            {validationStatus.issues.map((issue, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2 p-2 bg-red-50 border-l-4 border-red-500 rounded"
              >
                <FiAlertCircle className="text-red-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-red-700">{issue}</span>
              </div>
            ))}

            {validationStatus.warnings.map((warning, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2 p-2 bg-yellow-50 border-l-4 border-yellow-500 rounded"
              >
                <FiAlertCircle className="text-yellow-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-yellow-700">{warning}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment Scenario Alert */}
      <div
        className={`rounded-lg p-4 border-2 ${
          paymentScenario.type === "CUSTOMER_REFUND"
            ? "bg-orange-50 border-orange-300"
            : paymentScenario.type === "ZERO_PAYMENT"
            ? "bg-green-50 border-green-300"
            : "bg-blue-50 border-blue-300"
        }`}
      >
        <div className="flex gap-3">
          <paymentScenario.icon
            className={`flex-shrink-0 mt-0.5 text-xl ${
              paymentScenario.type === "CUSTOMER_REFUND"
                ? "text-orange-600"
                : paymentScenario.type === "ZERO_PAYMENT"
                ? "text-green-600"
                : "text-blue-600"
            }`}
          />
          <div className="flex-1">
            <p
              className={`font-semibold ${
                paymentScenario.type === "CUSTOMER_REFUND"
                  ? "text-orange-800"
                  : paymentScenario.type === "ZERO_PAYMENT"
                  ? "text-green-800"
                  : "text-blue-800"
              }`}
            >
              {paymentScenario.label}
            </p>
            <p
              className={`text-sm mt-1 ${
                paymentScenario.type === "CUSTOMER_REFUND"
                  ? "text-orange-700"
                  : paymentScenario.type === "ZERO_PAYMENT"
                  ? "text-green-700"
                  : "text-blue-700"
              }`}
            >
              {paymentScenario.description}
            </p>
            {paymentScenario.type === "CUSTOMER_REFUND" && (
              <>
                <p className="text-xl font-bold mt-2 text-orange-600">
                  Refund Amount: ₹
                  {paymentScenario.refundAmount.toLocaleString()}
                </p>
                {requiresRefundApproval && (
                  <p className="text-xs mt-1 text-orange-600 font-medium">
                    ⚠️ Manager approval required (amount &gt; ₹10,000)
                  </p>
                )}
              </>
            )}
            {paymentScenario.type === "CUSTOMER_PAYS" && (
              <p className="text-xl font-bold mt-2 text-blue-600">
                Amount to Pay: ₹{paymentScenario.amount.toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-4">
          {/* Customer Info */}
          <CustomerInfoCard
            customer={customerInfo}
            isAnonymous={isAnonymous}
            session={session}
          />

          {/* Cart Items - Only show if not gold purchase */}
          {paymentScenario.transactionType !== "GOLD_PURCHASE" &&
            cartItems.length > 0 && (
              <CartItemsCard
                items={cartItems}
                sessionPricing={sessionPricing}
                session={session}
              />
            )}

          {/* Exchange Items */}
          {exchangeItems.length > 0 && (
            <ExchangeItemsCard
              items={exchangeItems}
              sessionPricing={sessionPricing}
              isGoldPurchase={
                paymentScenario.transactionType === "GOLD_PURCHASE"
              }
            />
          )}

          {/* Applied Adjustments */}
          {session?.priceAdjustments?.filter((adj) => adj.status === "ACTIVE")
            .length > 0 && (
            <AdjustmentsCard
              adjustments={session.priceAdjustments.filter(
                (adj) => adj.status === "ACTIVE"
              )}
            />
          )}

          {/* Price Lock Info */}
          {session?.priceLock?.isLocked && (
            <PriceLockCard priceLock={session.priceLock} />
          )}
        </div>

        {/* Right Column - Summary & Action */}
        <div className="space-y-4">
          {/* Order Summary */}
          <OrderSummaryCard
            summary={summary}
            paymentScenario={paymentScenario}
          />

          {/* Place Order Button */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <Button
              onClick={handlePlaceOrder}
              disabled={!validationStatus.canPlaceOrder || processing}
              className="w-full"
              size="lg"
            >
              <FiCheckCircle className="mr-2" />
              {processing ? "Processing..." : "Place Order"}
            </Button>

            {!validationStatus.canPlaceOrder && (
              <p className="text-xs text-red-600 text-center mt-2">
                Resolve all issues to continue
              </p>
            )}
          </div>

          {/* Session Info */}
          <SessionInfoCard session={session} />
        </div>
      </div>

      {/* Order Placement Modal */}
      <OrderPlacementModal
        isOpen={showPlacementModal}
        onClose={() => setShowPlacementModal(false)}
        onConfirm={handleConfirmOrder}
        session={session}
        customer={customerInfo}
        isAnonymous={isAnonymous}
        summary={summary}
        processing={processing}
        primaryBranch={primaryBranch}
      />
    </div>
  );
};

// ============================================================================
// ✅ CONVERTED SESSION VIEW
// ============================================================================
// ✅ FIXED: ConvertedSessionView - Uses outcome data instead of pricing
const ConvertedSessionView = ({
  session,
  paymentScenario,
  cartItems,
  exchangeItems,
  customerInfo,
  isAnonymous,
  summary,
  onViewOrderDetails,
}) => {
  const outcome = session?.outcome || {};

  // ✅ Calculate proper values from outcome
  const orderValue = outcome.orderValue || 0;
  const exchangeValue = outcome.totalExchangeValue || 0;
  const netAmount = orderValue - exchangeValue; // Can be negative for refund

  // ✅ Determine transaction type from outcome
  const hasProducts = (outcome.itemsPurchased || 0) > 0;
  const hasExchange = (outcome.exchangeItemsAccepted || 0) > 0;

  let transactionType;
  let displayScenario;

  if (!hasProducts && hasExchange) {
    transactionType = "GOLD_PURCHASE";
    displayScenario = {
      type: "CUSTOMER_REFUND",
      label: "Customer Received Refund",
      amount: Math.abs(exchangeValue),
      color: "orange",
      icon: FiRefreshCw,
    };
  } else if (hasProducts && hasExchange) {
    if (netAmount < 0) {
      transactionType = "EXCHANGE_SALE_WITH_REFUND";
      displayScenario = {
        type: "CUSTOMER_REFUND",
        label: "Customer Received Refund",
        amount: Math.abs(netAmount),
        color: "orange",
        icon: FiRefreshCw,
      };
    } else if (netAmount === 0) {
      transactionType = "EXCHANGE_SALE_EVEN";
      displayScenario = {
        type: "ZERO_PAYMENT",
        label: "No Payment Required",
        amount: 0,
        color: "green",
        icon: FiCheckCircle,
      };
    } else {
      transactionType = "EXCHANGE_SALE";
      displayScenario = {
        type: "CUSTOMER_PAYS",
        label: "Customer Paid",
        amount: netAmount,
        color: "blue",
        icon: FiDollarSign,
      };
    }
  } else {
    transactionType = "SALE";
    displayScenario = {
      type: "CUSTOMER_PAYS",
      label: "Customer Paid",
      amount: orderValue,
      color: "blue",
      icon: FiDollarSign,
    };
  }

  console.log("📊 Converted View Data:", {
    orderValue,
    exchangeValue,
    netAmount,
    transactionType,
    displayScenario,
  });

  return (
    <div className="space-y-4">
      {/* Success Header */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-green-100 rounded-full">
            <FiCheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-green-900 mb-1">
              Order Successfully Placed!
            </h2>
            <p className="text-green-700 mb-4">
              This consultation session has been converted to an order
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-lg p-3">
                <p className="text-sm text-gray-600 mb-1">Order ID</p>
                <p className="font-bold text-gray-900">{outcome.orderId}</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-sm text-gray-600 mb-1">Order Type</p>
                <p className="font-bold text-gray-900">
                  {transactionType === "GOLD_PURCHASE"
                    ? "Gold Purchase"
                    : transactionType === "EXCHANGE_SALE"
                    ? "Exchange Sale"
                    : transactionType === "EXCHANGE_SALE_WITH_REFUND"
                    ? "Exchange Sale (Refund)"
                    : "Sale"}
                </p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-sm text-gray-600 mb-1">Created At</p>
                <p className="font-bold text-gray-900">
                  {new Date(outcome.orderCreatedAt).toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>

            <Button
              onClick={onViewOrderDetails}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
            >
              <FiExternalLink />
              View Full Order Details
            </Button>
          </div>
        </div>
      </div>

      {/* Order Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiShoppingCart className="text-primary" />
            <p className="text-sm text-gray-600">Items Purchased</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {outcome.itemsPurchased || 0}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiRefreshCw
              className={hasExchange ? "text-orange-600" : "text-gray-400"}
            />
            <p className="text-sm text-gray-600">Exchange Items</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {outcome.exchangeItemsAccepted || 0}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiDollarSign className="text-blue-600" />
            <p className="text-sm text-gray-600">Order Value</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ₹{orderValue.toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <displayScenario.icon
              className={`text-${displayScenario.color}-600`}
            />
            <p className="text-sm text-gray-600">
              {displayScenario.type === "CUSTOMER_REFUND"
                ? "Refund Given"
                : hasExchange
                ? "Exchange Value"
                : "Amount Paid"}
            </p>
          </div>
          <p className={`text-2xl font-bold text-${displayScenario.color}-600`}>
            ₹
            {displayScenario.type === "CUSTOMER_REFUND"
              ? displayScenario.amount.toLocaleString()
              : hasExchange
              ? exchangeValue.toLocaleString()
              : displayScenario.amount.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Payment Scenario Summary */}
      <div
        className={`rounded-lg p-4 border-2 ${
          displayScenario.color === "orange"
            ? "bg-orange-50 border-orange-300"
            : displayScenario.color === "green"
            ? "bg-green-50 border-green-300"
            : "bg-blue-50 border-blue-300"
        }`}
      >
        <div className="flex items-center gap-3">
          <displayScenario.icon
            className={`text-2xl ${
              displayScenario.color === "orange"
                ? "text-orange-600"
                : displayScenario.color === "green"
                ? "text-green-600"
                : "text-blue-600"
            }`}
          />
          <div className="flex-1">
            <p
              className={`font-semibold text-lg ${
                displayScenario.color === "orange"
                  ? "text-orange-800"
                  : displayScenario.color === "green"
                  ? "text-green-800"
                  : "text-blue-800"
              }`}
            >
              {displayScenario.label}
            </p>
            <p
              className={`text-2xl font-bold mt-1 text-${displayScenario.color}-600`}
            >
              ₹{displayScenario.amount.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Order Summary Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - Order Details */}
        <div className="lg:col-span-2 space-y-4">
          {/* Customer Info */}
          <CustomerInfoCard
            customer={customerInfo}
            isAnonymous={isAnonymous}
            session={session}
          />

          {/* Order Items Summary */}
          {hasProducts && (
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
                <FiPackage className="text-primary" />
                Items in Order ({outcome.itemsPurchased})
              </h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <FiCheckCircle className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                <p className="text-blue-900 font-semibold">
                  {outcome.itemsPurchased}{" "}
                  {outcome.itemsPurchased === 1 ? "item" : "items"} purchased
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  View full order details to see all items
                </p>
              </div>
            </div>
          )}

          {/* Exchange Items Summary */}
          {hasExchange && (
            <div
              className={`bg-white rounded-lg shadow-md p-4 ${
                transactionType === "GOLD_PURCHASE"
                  ? "border-2 border-orange-300"
                  : ""
              }`}
            >
              <h3
                className={`text-lg font-semibold flex items-center gap-2 mb-4 ${
                  transactionType === "GOLD_PURCHASE"
                    ? "text-orange-800"
                    : "text-gray-800"
                }`}
              >
                {transactionType === "GOLD_PURCHASE" ? (
                  <>
                    <FiRefreshCw className="text-orange-600" />
                    Gold Purchase Completed ({outcome.exchangeItemsAccepted})
                  </>
                ) : (
                  <>
                    <FiTag className="text-green-600" />
                    Exchange Items Accepted ({outcome.exchangeItemsAccepted})
                  </>
                )}
              </h3>

              {transactionType === "GOLD_PURCHASE" && (
                <div className="mb-3 p-2 bg-orange-50 border border-orange-200 rounded text-sm text-orange-800">
                  <p className="font-medium">Customer Sold Gold to Business</p>
                  <p className="text-xs mt-1">
                    Payment processed for ₹{exchangeValue.toLocaleString()}
                  </p>
                </div>
              )}

              <div
                className={`p-4 rounded-lg ${
                  transactionType === "GOLD_PURCHASE"
                    ? "bg-orange-50 border border-orange-200"
                    : "bg-green-50 border border-green-200"
                }`}
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">
                    {transactionType === "GOLD_PURCHASE"
                      ? "Total Paid to Customer:"
                      : "Total Exchange Value:"}
                  </span>
                  <span
                    className={`text-xl font-bold ${
                      transactionType === "GOLD_PURCHASE"
                        ? "text-orange-700"
                        : "text-green-700"
                    }`}
                  >
                    ₹{exchangeValue.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Summary */}
        <div className="space-y-4">
          {/* Final Summary */}
          <div className="bg-white rounded-lg shadow-md p-4 sticky top-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
              <FiDollarSign className="text-primary" />
              Order Summary
            </h3>

            <div className="space-y-3">
              {/* Order Value */}
              {hasProducts && (
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-700">Order Value:</span>
                  <span className="text-lg font-bold text-gray-900">
                    ₹{orderValue.toLocaleString()}
                  </span>
                </div>
              )}

              {/* Exchange Value */}
              {hasExchange && (
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-700">
                    {transactionType === "GOLD_PURCHASE"
                      ? "Gold Purchase:"
                      : "Exchange Credit:"}
                  </span>
                  <span
                    className={`text-lg font-bold ${
                      transactionType === "GOLD_PURCHASE"
                        ? "text-orange-600"
                        : "text-green-600"
                    }`}
                  >
                    {transactionType === "GOLD_PURCHASE" ? "" : "-"}₹
                    {exchangeValue.toLocaleString()}
                  </span>
                </div>
              )}

              {/* Final Amount */}
              <div
                className={`-mx-4 px-4 py-4 rounded-lg ${
                  displayScenario.color === "orange"
                    ? "bg-orange-50"
                    : displayScenario.color === "green"
                    ? "bg-green-50"
                    : "bg-blue-50"
                }`}
              >
                <div className="flex justify-between items-center">
                  <span
                    className={`font-semibold ${
                      displayScenario.color === "orange"
                        ? "text-orange-800"
                        : displayScenario.color === "green"
                        ? "text-green-800"
                        : "text-blue-800"
                    }`}
                  >
                    {displayScenario.type === "CUSTOMER_REFUND"
                      ? "Refund Given:"
                      : displayScenario.type === "ZERO_PAYMENT"
                      ? "Balance:"
                      : "Amount Paid:"}
                  </span>
                  <span
                    className={`text-2xl font-bold ${
                      displayScenario.color === "orange"
                        ? "text-orange-600"
                        : displayScenario.color === "green"
                        ? "text-green-600"
                        : "text-blue-600"
                    }`}
                  >
                    ₹{displayScenario.amount.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-gray-500 text-center">
                {displayScenario.type === "CUSTOMER_REFUND"
                  ? "Refund has been processed"
                  : displayScenario.type === "ZERO_PAYMENT"
                  ? "No payment was required"
                  : "Payment has been completed"}
              </p>
            </div>
          </div>

          {/* View Order Button */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <Button
              onClick={onViewOrderDetails}
              className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark"
              size="lg"
            >
              <FiExternalLink />
              View Full Order Details
            </Button>
          </div>

          {/* Session Info */}
          <SessionInfoCard session={session} />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CUSTOMER INFO CARD
// ============================================================================
const CustomerInfoCard = ({ customer, isAnonymous, session }) => {
  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-5 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
              <FiUser className="text-primary text-lg" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">
              Customer Information
            </h3>
          </div>
          {isAnonymous && (
            <span className="px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1.5">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6z" />
              </svg>
              Guest Checkout
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-1">
        <InfoRow
          icon={<FiUser />}
          label="Name"
          value={customer?.name || "Not provided"}
        />
        <div className="border-t border-gray-100"></div>

        <InfoRow
          icon={<FiPhone />}
          label="Phone"
          value={customer?.phoneNumber || "Not provided"}
        />
        <div className="border-t border-gray-100"></div>

        {customer?.email && (
          <>
            <InfoRow icon={<FiMail />} label="Email" value={customer.email} />
            <div className="border-t border-gray-100"></div>
          </>
        )}

        {!isAnonymous && customer?.customerProfile?.type && (
          <InfoRow
            icon={<FiTag />}
            label="Customer Type"
            value={customer.customerProfile.type}
          />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// ENHANCED INFO ROW COMPONENT
// ============================================================================
const InfoRow = ({ icon, label, value }) => {
  return (
    <div className="flex items-center gap-4 py-3 px-1 hover:bg-gray-50 rounded-lg transition-colors duration-200 group">
      {/* Icon Container */}
      {icon && (
        <div className="w-9 h-9 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:from-primary/20 group-hover:to-primary/10 transition-all duration-200">
          <span className="text-primary text-base">{icon}</span>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
          {label}
        </p>
        <p className="text-sm font-semibold text-gray-800 truncate">{value}</p>
      </div>
    </div>
  );
};

// ============================================================================
// CART ITEMS CARD
// ============================================================================
const CartItemsCard = ({ items, sessionPricing, session }) => {
  const pricingItems = sessionPricing?.items || [];

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
        <FiPackage className="text-primary" />
        Order Items ({items.length})
      </h3>

      <div className="space-y-3">
        {items.map((item, idx) => {
          const itemPricing = pricingItems.find(
            (p) => p.productId?.toString() === item.product._id?.toString()
          );
          const pricing =
            itemPricing?.finalPricing || itemPricing?.originalPricing;
          const breakdown = pricing?.breakdown || {};

          return (
            <div
              key={idx}
              className="p-3 border border-gray-200 rounded-lg hover:border-primary/30 transition-colors"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800">
                    {item.product?.name}
                  </h4>
                  <div className="flex gap-3 text-xs text-gray-600 mt-1">
                    <span>SKU: {item.product?.sku}</span>
                    <span>Qty: {item.quantity}</span>
                    {item.product?.grossWeight && (
                      <span>Weight: {item.product.grossWeight}g</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">
                    ₹
                    {(
                      (breakdown.finalAmount || 0) * item.quantity
                    ).toLocaleString()}
                  </p>
                  {itemPricing?.adjustments?.length > 0 && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                      Adjusted
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs text-gray-600 pt-2 border-t border-gray-100">
                <div>
                  Metal: ₹{(breakdown.metalValue || 0).toLocaleString()}
                </div>
                <div>
                  Making: ₹{(breakdown.charges?.making || 0).toLocaleString()}
                </div>
                {breakdown.stoneCharges > 0 && (
                  <div>Stones: ₹{breakdown.stoneCharges.toLocaleString()}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// EXCHANGE ITEMS CARD
// ============================================================================
const ExchangeItemsCard = ({ items, sessionPricing, isGoldPurchase }) => {
  const acceptedItems = items.filter((item) => item.customerAccepted);

  if (acceptedItems.length === 0) return null;

  return (
    <div
      className={`bg-white rounded-lg shadow-md p-4 ${
        isGoldPurchase ? "border-2 border-orange-300" : ""
      }`}
    >
      <h3
        className={`text-lg font-semibold flex items-center gap-2 mb-4 ${
          isGoldPurchase ? "text-orange-800" : "text-gray-800"
        }`}
      >
        {isGoldPurchase ? (
          <>
            <FiRefreshCw className="text-orange-600" />
            Gold Purchase Items ({acceptedItems.length})
          </>
        ) : (
          <>
            <FiTag className="text-green-600" />
            Exchange Items ({acceptedItems.length})
          </>
        )}
      </h3>

      {isGoldPurchase && (
        <div className="mb-3 p-2 bg-orange-50 border border-orange-200 rounded text-sm text-orange-800">
          <p className="font-medium">Customer Selling Gold to Business</p>
          <p className="text-xs mt-1">
            Business will pay customer for these items
          </p>
        </div>
      )}

      <div className="space-y-2">
        {acceptedItems.map((item, idx) => (
          <div
            key={idx}
            className={`flex justify-between items-center p-3 border rounded-lg ${
              isGoldPurchase
                ? "bg-orange-50 border-orange-200"
                : "bg-green-50 border-green-200"
            }`}
          >
            <div>
              <p className="font-medium text-gray-800">
                {item.description || `Exchange Item ${idx + 1}`}
              </p>
              <div className="flex gap-3 text-xs text-gray-600 mt-1">
                {item.itemType && <span>{item.itemType}</span>}
                {item.metalDetails?.purity && (
                  <span>{item.metalDetails.purity}</span>
                )}
                {item.netWeight && <span>{item.netWeight}g</span>}
              </div>
              {item.conditionGrade && (
                <span className="text-xs text-gray-500">
                  Condition: {item.conditionGrade}
                </span>
              )}
            </div>
            <div className="text-right">
              <p
                className={`text-lg font-bold ${
                  isGoldPurchase ? "text-orange-700" : "text-green-700"
                }`}
              >
                ₹{(item.agreedValue || 0).toLocaleString()}
              </p>
              <span
                className={`text-xs ${
                  isGoldPurchase ? "text-orange-600" : "text-green-600"
                }`}
              >
                {isGoldPurchase ? "To Pay" : "Credit"}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div
        className={`mt-3 pt-3 border-t flex justify-between items-center ${
          isGoldPurchase ? "border-orange-200" : "border-green-200"
        }`}
      >
        <span className="font-medium text-gray-700">
          {isGoldPurchase ? "Total Payment Due:" : "Total Exchange Value:"}
        </span>
        <span
          className={`text-xl font-bold ${
            isGoldPurchase ? "text-orange-700" : "text-green-700"
          }`}
        >
          ₹{(sessionPricing?.summary?.totalExchangeValue || 0).toLocaleString()}
        </span>
      </div>
    </div>
  );
};

// ============================================================================
// ADJUSTMENTS CARD
// ============================================================================
const AdjustmentsCard = ({ adjustments }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-3">
        <FiPercent className="text-primary" />
        Applied Discounts & Adjustments ({adjustments.length})
      </h3>

      <div className="space-y-2">
        {adjustments.map((adj, idx) => (
          <div
            key={idx}
            className="flex items-start justify-between p-2 bg-green-50 border border-green-200 rounded"
          >
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800">{adj.reason}</p>
              <div className="text-xs text-gray-600 mt-0.5">
                <span className="font-medium">
                  {adj.adjustment.type === "PERCENTAGE"
                    ? `${adj.adjustment.value}% off`
                    : adj.adjustment.type === "OVERRIDE_PRICE"
                    ? `Override: ₹${adj.adjustment.overridePrice}`
                    : `₹${adj.adjustment.value} off`}
                </span>
                <span className="mx-1">•</span>
                <span>{adj.scope}</span>
              </div>
            </div>
            {adj.approved && (
              <FiCheckCircle className="text-green-600 flex-shrink-0 mt-0.5" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// PRICE LOCK CARD
// ============================================================================
const PriceLockCard = ({ priceLock }) => {
  const isExpired = priceLock.lockExpiresAt
    ? new Date(priceLock.lockExpiresAt) < new Date()
    : false;

  return (
    <div
      className={`bg-white rounded-lg shadow-md p-4 border-2 ${
        isExpired ? "border-red-300" : "border-yellow-300"
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        <FiLock
          className={isExpired ? "text-red-600" : "text-yellow-600"}
          size={20}
        />
        <h3 className="text-lg font-semibold text-gray-800">
          {isExpired ? "Price Lock Expired" : "Prices Locked"}
        </h3>
      </div>

      <div className="space-y-2 text-sm">
        <InfoRow
          label="Locked By"
          value={priceLock.lockedBy}
          className="text-sm"
        />
        <InfoRow
          label="Locked At"
          value={new Date(priceLock.lockedAt).toLocaleString()}
          className="text-sm"
        />
        {priceLock.lockExpiresAt && (
          <InfoRow
            label={isExpired ? "Expired At" : "Expires At"}
            value={new Date(priceLock.lockExpiresAt).toLocaleTimeString()}
            className={`text-sm ${isExpired ? "text-red-600" : ""}`}
          />
        )}
      </div>

      {!isExpired && (
        <div className="mt-3 p-2 bg-yellow-50 rounded text-xs text-yellow-700">
          <FiClock className="inline mr-1" />
          Prices are guaranteed until expiry
        </div>
      )}

      {isExpired && (
        <div className="mt-3 p-2 bg-red-50 rounded text-xs text-red-700">
          <FiAlertCircle className="inline mr-1" />
          Lock expired - prices may have changed
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ORDER SUMMARY CARD
// ============================================================================
const OrderSummaryCard = ({ summary, paymentScenario }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-4 sticky top-4">
      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
        <FiDollarSign className="text-primary" />
        Order Summary
      </h3>

      <div className="space-y-2 text-sm">
        {/* Show subtotal only if not gold purchase */}
        {paymentScenario.transactionType !== "GOLD_PURCHASE" && (
          <SummaryLine label="Subtotal" value={summary.subtotal} />
        )}

        {summary.totalDiscount > 0 && (
          <SummaryLine
            label="Item Discounts"
            value={-summary.totalDiscount}
            className="text-green-600"
          />
        )}

        {summary.totalAdjustments > 0 && (
          <SummaryLine
            label="Additional Discounts"
            value={-summary.totalAdjustments}
            className="text-green-600"
          />
        )}

        {paymentScenario.transactionType !== "GOLD_PURCHASE" && (
          <>
            <SummaryLine label="GST (3%)" value={summary.totalGST} />

            <div className="border-t pt-2 mt-2">
              <SummaryLine
                label="Grand Total"
                value={summary.grandTotal}
                className="font-semibold text-base"
              />
            </div>
          </>
        )}

        {summary.totalExchangeValue > 0 && (
          <>
            <div className="border-t pt-2">
              <SummaryLine
                label={
                  paymentScenario.transactionType === "GOLD_PURCHASE"
                    ? "Gold Purchase Value"
                    : "Exchange Credit"
                }
                value={
                  paymentScenario.transactionType === "GOLD_PURCHASE"
                    ? summary.totalExchangeValue
                    : -summary.totalExchangeValue
                }
                className={
                  paymentScenario.transactionType === "GOLD_PURCHASE"
                    ? "text-orange-600 font-medium"
                    : "text-green-600 font-medium"
                }
              />
            </div>

            <div
              className={`-mx-4 px-4 py-3 rounded mt-2 ${
                paymentScenario.type === "CUSTOMER_REFUND"
                  ? "bg-orange-50"
                  : paymentScenario.type === "ZERO_PAYMENT"
                  ? "bg-green-50"
                  : "bg-primary/5"
              }`}
            >
              <SummaryLine
                label={
                  paymentScenario.type === "CUSTOMER_REFUND"
                    ? "Refund Due"
                    : paymentScenario.type === "ZERO_PAYMENT"
                    ? "Balance"
                    : "Net Payable"
                }
                value={Math.abs(summary.netPayable)}
                className={`font-bold text-lg ${
                  paymentScenario.type === "CUSTOMER_REFUND"
                    ? "text-orange-600"
                    : paymentScenario.type === "ZERO_PAYMENT"
                    ? "text-green-600"
                    : "text-primary"
                }`}
              />
            </div>
          </>
        )}
      </div>

      {/* Payment scenario note */}
      <div className="mt-4 pt-4 border-t">
        <p className="text-xs text-gray-500 text-center">
          {paymentScenario.type === "CUSTOMER_REFUND"
            ? "Customer will receive refund"
            : paymentScenario.type === "ZERO_PAYMENT"
            ? "No payment required"
            : "Amount to be collected from customer"}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// SESSION INFO CARD
// ============================================================================
const SessionInfoCard = ({ session }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">
        Session Details
      </h3>
      <div className="space-y-2 text-xs text-gray-600">
        <InfoRow
          label="Session Code"
          value={session?.sessionCode}
          className="text-xs"
        />
        <InfoRow
          label="Type"
          value={session?.sessionType}
          className="text-xs"
        />
        <InfoRow
          label="Branch"
          value={session?.branchCode}
          className="text-xs"
        />
        {session?.startTime && (
          <InfoRow
            label="Started"
            value={new Date(session.startTime).toLocaleTimeString()}
            className="text-xs"
          />
        )}
        {session?.duration && (
          <InfoRow
            label="Duration"
            value={`${session.duration} min`}
            className="text-xs"
          />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================
// const InfoRow = ({ icon, label, value, className = "" }) => (
//   <div className={`flex items-center justify-between ${className}`}>
//     {icon && <span className="text-gray-500 mr-2">{icon}</span>}
//     <span className="text-gray-600">{label}:</span>
//     <span className="font-medium text-gray-800">{value}</span>
//   </div>
// );

const SummaryLine = ({ label, value, className = "" }) => (
  <div className={`flex justify-between items-center ${className}`}>
    <span>{label}:</span>
    <span>
      {value < 0 ? "-" : ""}₹{Math.abs(value || 0).toLocaleString()}
    </span>
  </div>
);

export default OrderReviewTab;
